import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import translate from "@insite/client-framework/Translate";
import { AttributeValueModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@tecumseh/Overrides/Components/ProductDescription";

import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
// import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Badge from "@insite/mobius/Badge";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps, StyledIcon } from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ProductImage, { ProductImageStyles } from "@tecumseh/Overrides/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@tecumseh/Overrides/Components/ProductPartNumbers";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
    carouselType?: string;
    website?: string;
    showAttributes?: boolean;
    relatedProductType?: string;
    selectedProduct?: string;
    setSelectedProduct: (value: string) => void;
    isImageClickable?: boolean;
    isTitleClickable?: boolean;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    mainContainer?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    accessoryTitleStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    learnMoreGridItem?: GridItemProps;
    learnMoreButtonStyle?: LinkPresentationProps;
    productNameWrapper?: InjectableCss;
    attributeTypeGridItem?: InjectableCss;
    attributeValueGridItem?: InjectableCss;
    attributeTypeText?: TypographyPresentationProps;
    attributeValueText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    semicolon?: InjectableCss;
    container?: InjectableCss;
    imageWrapper?: InjectableCss;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    attributeTypeGridItemMF?: InjectableCss;
    attributeTypeTextMF?: TypographyPresentationProps;
    learnMoreButtonStyleMF?: LinkPresentationProps;
    bundleProductMainContainer?: InjectableCss;
    accessoryPartNumbersStyles?: ProductPartNumbersStyles;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    mainContainer: {
        css: css`
            border: 1px solid ${COLORS.graySwan};
            padding: 10px;
            flex: 1;
        `,
    },
    bundleProductMainContainer: {
        css: css`
            border: 1px solid ${COLORS.graySwan};
            padding: 10px;
        `,
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                min-height: 150px;
            `,
        },
        img: {
            css: css`
                width: 100%;
                height: 100%;
                object-fit: contain;
                object-position: center top;
                aspect-ratio: 1/1;
            `,
        },
    },
    productBrandWrapper: {
        css: css`
            margin: 6px 0;
            min-height: 22px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 6px;
                text-align: center;
                > span {
                    font-size: ${TYPOGRAPHY.size.small};
                    color: ${COLORS.primaryBlue} !important;
                    font-family: ${TYPOGRAPHY.fontFamily.base};
                    &:hover {
                        color: ${COLORS.secondaryNavyOne} !important;
                    }
                }
            `,
        },
    },
    accessoryTitleStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 6px;
                text-align: center;
                margin-bottom: 14px;
                > span {
                    font-size: ${TYPOGRAPHY.size.smaller};
                    color: ${getColor("secondary")} !important;
                    font-weight: 500;
                    &:hover {
                        color: ${getColor("secondary")} !important;
                    }
                }
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 6px;
                span {
                    text-align: center;
                    font-weight: 600 !important;
                }
            `,
        },
        erpNumberGridItem: {
            css: css`
                justify-content: center;
            `,
        },
        erpNumberLabelText: {
            css: css`
                font-size: ${TYPOGRAPHY.size.smallest} !important;
                font-family: ${TYPOGRAPHY.fontFamily.base};
            `,
        },
        erpNumberValueText: {
            css: css`
                flex: 0 1 auto;
                font-size: ${TYPOGRAPHY.size.smallest};
                font-family: ${TYPOGRAPHY.fontFamily.base};
            `,
        },
    },
    accessoryPartNumbersStyles: {
        erpNumberGridItem: {
            css: css`
                justify-content: center;
            `,
        },
        erpNumberValueTextRelatedProd: {
            size: TYPOGRAPHY.size.large,
            color: "secondary",
            weight: 500,
        },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 6px;
            text-align: center;
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    addToCartContainer: {
        gap: 10,
        css: css`
            flex-grow: 0;
            margin-bottom: 6px;
        `,
    },
    qtyGridItem: { width: 3 },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
            `,
        },
    },
    addToCartButtonGridItem: { width: 9 },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 13 },
            css: css`
                margin-bottom: 12px;
            `,
        },
    },
    learnMoreGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            margin-top: 10px !important;
        `,
    },
    learnMoreButtonStyle: {
        css: css`
            border: 1px solid ${COLORS.background.yellow};
            background-color: ${COLORS.background.yellow};
            padding: 0 21px;
            border-radius: 25px;
            min-height: 30px;
            > span {
                color: ${COLORS.black} !important;
                font-family: ${TYPOGRAPHY.fontFamily.base};
            }
            &:hover {
                border: 1px solid ${COLORS.background.mango};
                background-color: ${COLORS.background.mango};
            }
        `,
    },
    learnMoreButtonStyleMF: {
        css: css`
            border: 1px solid ${COLORS.lightPurple};
            background-color: ${COLORS.lightPurple};
            padding: 0 21px;
            border-radius: 25px;
            min-height: 30px;
            > span {
                color: ${COLORS.white} !important;
                font-family: ${TYPOGRAPHY.fontFamily.base};
            }
            &:hover {
                border: 1px solid ${getColor("primary")};
                background-color: ${getColor("primary")};
            }
        `,
    },
    productNameWrapper: {
        css: css`
            width: 100%;
            justify-content: center;
            text-align: center;
        `,
    },
    attributeTypeGridItem: {
        // width: [6, 6, 6, 4, 6],
        css: css`
            overflow: hidden;
            opacity: 1;
            color: ${COLORS.secondaryNavyOne};
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-size: ${TYPOGRAPHY.size.smallest};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smaller};
            text-align: center;
        `,
    },
    attributeTypeGridItemMF: {
        css: css`
            overflow: hidden;
            opacity: 1;
            color: ${COLORS.secondaryNavyOne};
            font-size: ${TYPOGRAPHY.size.smallest};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smaller};
            text-align: center;
            margin-bottom: 5px;
        `,
    },
    attributeValueGridItem: {
        // width: [6, 6, 6, 8, 6],
        css: css`
            opacity: 1;
            color: ${COLORS.black};
            font-size: ${TYPOGRAPHY.size.tiny};
            font-weight: ${TYPOGRAPHY.weight.base};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smaller};
            text-align: center;
        `,
    },
    attributeTypeText: {
        weight: "bold",
        css: css`
            font-size: ${TYPOGRAPHY.size.smallest};
        `,
    },
    attributeTypeTextMF: {
        weight: "bold",
        color: COLORS.black,
        css: css`
            font-size: ${TYPOGRAPHY.size.tiny};
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            text-align: center;
            justify-content: center;
        `,
    },
    semicolon: {
        css: css`
            padding-left: 2px;
            padding-right: 2px;
            color: ${COLORS.secondaryNavyOne};
            font-size: ${TYPOGRAPHY.size.smallest};
        `,
    },
    container: {
        css: css`
            width: 100%;
        `,
    },
    imageWrapper: {
        css: css`
            height: 210px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-height: 250px;
                        max-height: 250px;
                    `,
                    css`
                        min-height: 250px;
                        max-height: 250px;
                    `,
                    null,
                    null,
                    null,
                ])};
        `,
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    carouselType,
    website,
    showAttributes,
    relatedProductType,
    selectedProduct,
    setSelectedProduct,
    isImageClickable,
    isTitleClickable,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    const attributes = product.attributeTypes?.map(attr => {
        return attr;
    });

    const limitedAttributeTypes = attributes?.slice(0, 10);

    const displayAttributeValues = (attributeValues: AttributeValueModel[] | null) => {
        return (
            <Typography {...styles.attributeValueText}>
                {(attributeValues ?? []).map(a => a.valueDisplay).join(", ")}
            </Typography>
        );
    };

    const attributeTypeGridItemStyle =
        website && website === "masterflux" ? styles.attributeTypeGridItemMF : styles.attributeTypeGridItem;
    const attributeTypeTextStyle =
        website && website === "masterflux" ? styles.attributeTypeTextMF : styles.attributeTypeText;
    const learnMoreButtonStyle =
        website && website === "masterflux" ? styles.learnMoreButtonStyleMF : styles.learnMoreButtonStyle;

    const bundleProductMainContainerStyles: InjectableCss = {
        css: css`
            border: 1px solid ${product.id === selectedProduct ? getColor("primary") : COLORS.graySwan};
            padding: 10px;
            background: ${product.id === selectedProduct ? COLORS.darkGrey : "none"};
            flex: 1;
            &:hover {
                border: 1px solid ${getColor("primary")};

                background: ${COLORS.darkGrey};
            }
        `,
    };

    const mainContainerStyles =
        website === "masterflux" && (relatedProductType === "Bundle" || relatedProductType === "Accessory")
            ? bundleProductMainContainerStyles
            : styles.mainContainer;

    return (
        <StyledWrapper
            {...mainContainerStyles}
            className="mainContainer"
            id="replacement-carousel"
            onClick={() => setSelectedProduct(product.id)}
        >
            <ProductContext.Provider value={productContext}>
                {showImage && !showOverlayBadge && (
                    <StyledWrapper {...styles.imageWrapper}>
                        <ProductImage
                            product={productContext}
                            onClick={() => trackProductRecommendation(true)}
                            extendedStyles={styles.productImageStyles}
                            isClickable={isImageClickable}
                        />
                    </StyledWrapper>
                )}
                {showImage && showOverlayBadge && overlayBadge && (
                    <StyledWrapper
                    {...styles.badgeAreaWrapper}
                    ref={badgeAreaWrapperRef}
                    data-test-selector="productCarouselBadge"
                >
                        <ProductImage
                            extendedStyles={styles.productImageStyles}
                            product={productContext}
                            isClickable={isImageClickable}
                        />
                        <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                            <Badge
                                type={overlayBadge.badgeType}
                                value={
                                    overlayBadge.badgeType === "Image"
                                        ? overlayBadge.largeImageBadgePath
                                        : overlayBadge.displayText
                                }
                                badgeTextColor={overlayBadge.textColorHexCode}
                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                                textBadgeCss={styles.badgeText}
                                imageBadgeProps={styles.badgeImageProps}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                {showBrand && product.brand && (
                    <StyledWrapper {...styles.productBrandWrapper}>
                        <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                    </StyledWrapper>
                )}
                {showPartNumbers && (
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        showCustomerName={false}
                        showManufacturerItem={false}
                        extendedStyles={
                            relatedProductType === "Accessory"
                                ? styles.accessoryPartNumbersStyles
                                : styles.productPartNumbersStyles
                        }
                        website={website}
                        carouselType={carouselType}
                    />
                )}
                {showTitle && (
                    <StyledWrapper {...styles.productNameWrapper}>
                        <ProductDescription
                            product={productContext}
                            onClick={() => trackProductRecommendation(true)}
                            extendedStyles={
                                relatedProductType === "Accessory"
                                    ? styles.accessoryTitleStyles
                                    : styles.productDescriptionStyles
                            }
                            isClickable={isTitleClickable}
                        />
                    </StyledWrapper>
                )}
                {showTextBadges && textBadges && (
                    <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                )}
                {showImageBadges && imageBadges && (
                    <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
                )}
                {showAttributes && (
                    <StyledWrapper {...styles.container}>
                        {limitedAttributeTypes?.map(attributeType => (
                            <React.Fragment key={attributeType.id.toString()}>
                                <StyledWrapper {...styles.wrapper}>
                                    <StyledWrapper {...attributeTypeGridItemStyle}>
                                        <Typography
                                            {...attributeTypeTextStyle}
                                            data-test-selector={`attributeType${attributeType.id}`}
                                        >
                                            {attributeType.label || attributeType.name}
                                        </Typography>
                                        <Typography {...styles.semicolon}>:</Typography>
                                        <Typography {...styles.attributeValueGridItem}>
                                            {displayAttributeValues(attributeType.attributeValues)}
                                        </Typography>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </React.Fragment>
                        ))}
                    </StyledWrapper>
                )}

                {showPrice && (
                    <StyledWrapper {...styles.productPriceWrapper}>
                        <VisuallyHidden as="label" htmlFor={`${carouselId}-uom`} id={`${carouselId}-uom-label`}>
                            {translate("U/M")}
                        </VisuallyHidden>
                        <ProductPrice showLabel={false} product={productContext} />
                        {product.unitOfMeasures && (
                            <ProductUnitOfMeasureSelect
                                labelOverride=""
                                extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                id={`${carouselId}-uom`}
                            />
                        )}
                        <ProductQuantityBreakPricing extendedStyles={styles.productQuantityBreakPricingStyles} />
                    </StyledWrapper>
                )}
                {showAddToCart && (
                    <GridContainer {...styles.addToCartContainer}>
                        <GridItem {...styles.qtyGridItem}>
                            <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                                {translate("QTY")}
                            </VisuallyHidden>
                            <ProductQuantityOrdered
                                labelOverride=""
                                extendedStyles={styles.productQuantityOrderedStyles}
                                id={`${carouselId}-qty`}
                            />
                        </GridItem>
                        <GridItem {...styles.addToCartButtonGridItem}>
                            <ProductAddToCartButton
                                onAddToCartClick={() => trackProductRecommendation(false)}
                                data-test-selector="addToCartBtn"
                                extendedStyles={styles.productAddToCartButtonStyles}
                            />
                        </GridItem>
                    </GridContainer>
                )}
                {showAddToList && (
                    <ProductAddToListLink
                        data-test-selector="addToListLink"
                        extendedStyles={styles.productAddToListLinkStyles}
                    />
                )}
                <GridItem {...styles.learnMoreGridItem}>
                    <Link href={productContext.productInfo.productDetailPath} {...learnMoreButtonStyle}>
                        {translate("Learn More")}
                    </Link>
                </GridItem>
            </ProductContext.Provider>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
